import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { SITE_DOMAIN, SUPPORT_EMAIL } from "@/lib/site";

const SECTIONS = [
  {
    title: "1. The Services",
    text: "Pull Up helps friends meet up in real life. The Services include friend groups and crews, smart invites, plans & events, real-time check-ins at bars and venues, deals & specials, venue discovery, and Pull Up for Business tools for venue owners. We may add, change, or remove features at any time.",
  },
  {
    title: "2. Eligibility",
    text: "You must be at least 18 years old to use the Services, and of legal drinking age in your jurisdiction to use venue, check-in, or deals features at licensed premises. By using the Services, you represent that you meet these requirements.",
  },
  {
    title: "3. Your Account",
    text: "You agree to provide accurate information when creating your account, to keep your login credentials confidential, and to take responsibility for all activity under your account. One account per person. We may suspend or terminate accounts that violate these Terms.",
  },
  {
    title: "4. Acceptable Use",
    items: [
      "Harass, threaten, or harm other users",
      "Use the Services to stalk or track anyone without their knowledge and consent",
      "Post false check-ins, fake plans, or fraudulent deals",
      "Scrape, copy, or reverse engineer any part of the Services",
      "Interfere with or disrupt the Services or other users' experience",
      "Use the Services for any unlawful purpose",
    ],
    lead: "You agree not to:",
  },
  {
    title: "5. Check-Ins, Location & Safety",
    text: "Check-ins and smart invites share your location only with the people and crews you choose, and you control that visibility in your settings. Pull Up is a coordination tool, not a safety service: always meet in public, look out for your crew, and drink responsibly. We are not responsible for the conduct of users or venues you connect with through the Services.",
  },
  {
    title: "6. Deals, Specials & Venues",
    text: "Deals and specials are posted by third-party venues or gathered from public sources. We do not guarantee the availability, accuracy, or honoring of any offer. Venues are solely responsible for their listings, their offers, and for complying with all applicable laws, including those governing alcohol service.",
  },
  {
    title: "7. Pull Up for Business",
    text: "Business accounts — including claiming a venue, posting specials, and accessing audience insights — may be subject to additional terms presented at signup. You must be authorized to represent the venue you claim, and you are responsible for the accuracy of your listings and offers.",
  },
  {
    title: "8. Intellectual Property",
    text: "The Services, the Pull Up name and logo, and all content we provide are owned by Pull Up and protected by applicable laws. You retain ownership of the content you post, but grant us a non-exclusive license to display and share it within the Services.",
  },
  {
    title: "9. Disclaimers",
    text: "The Services are provided \"as is\" and \"as available\" without warranties of any kind, express or implied. We do not guarantee that the Services will be uninterrupted, accurate, or error-free, or that any meetup, deal, or venue information will meet your expectations.",
  },
  {
    title: "10. Limitation of Liability",
    text: "To the maximum extent permitted by law, Pull Up is not liable for any indirect, incidental, special, or consequential damages — or any loss of data, profits, or goodwill — arising from your use of the Services or any gathering arranged through them.",
  },
  {
    title: "11. Termination",
    text: "You may delete your account at any time through your profile settings. We may suspend or terminate access for violations of these Terms. Provisions that by their nature should survive termination — including intellectual property, disclaimers, and limitation of liability — survive.",
  },
  {
    title: "12. Changes to These Terms",
    text: "We may update these Terms from time to time. We will notify you of material changes through the app or by email, and your continued use of the Services after changes take effect constitutes acceptance of the updated Terms.",
  },
  {
    title: "13. Contact Us",
    text: "Questions about these Terms? Reach us at:",
    contact: true,
  },
];

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-[#0A0E0D] text-[#F4F4F0]" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#4FE3E0] transition-colors duration-300 mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Back to {SITE_DOMAIN}
          </Link>

          <h1 className="font-bold tracking-tight text-4xl sm:text-5xl text-white">
            Terms of Service
          </h1>
          <p className="mt-3 text-sm font-medium tracking-wide text-[#4FE3E0]">
            Effective Date: December 1, 2023
          </p>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed">
            Welcome to Pull Up. These Terms of Service ("Terms") govern your use of the Pull Up
            mobile application, website, and services (collectively, the "Services"), operated by
            Pull Up ("we", "us"). By creating an account or using the Services, you agree to
            these Terms. If you don't agree, please don't use the Services.
          </p>
        </motion.div>

        <div className="mt-12">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.title}
              data-testid={`terms-section-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="border-t border-white/10 py-8"
            >
              <h2 className="font-bold tracking-tight text-xl sm:text-2xl text-white">{s.title}</h2>
              {s.lead && <p className="mt-3 text-sm sm:text-base text-zinc-400">{s.lead}</p>}
              {s.text && <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{s.text}</p>}

              {s.contact && (
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="terms-contact-email-link"
                  className="mt-3 inline-flex items-center gap-2 text-[#4FE3E0] hover:text-white transition-colors duration-300 font-medium"
                >
                  <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
                </a>
              )}

              {s.items && (
                <ul className="mt-2.5 space-y-2.5">
                  {s.items.map((item, ii) => (
                    <li key={ii} className="flex gap-3 text-sm sm:text-base leading-relaxed">
                      <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#4FE3E0] shrink-0" />
                      <span className="text-zinc-400">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8 mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link
            to="/"
            data-testid="terms-home-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#4FE3E0] text-black font-semibold text-sm px-6 py-3.5 hover:bg-[#F4F4F0] transition-colors duration-300"
          >
            Back to {SITE_DOMAIN}
          </Link>
          <Link
            to="/privacy"
            data-testid="terms-privacy-link"
            className="text-sm text-zinc-400 hover:text-[#4FE3E0] transition-colors duration-300"
          >
            Read the Privacy Policy
          </Link>
        </div>
      </div>
    </main>
  );
}
